import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Modal } from '../../../components/ui/Modal';
import { dataExportGetManualExportReadme } from '../../../api';
import chamberFluxReadme from '../../../docs/chamber-flux-export-readme.md?raw';
import sensorDataReadme from '../../../docs/sensor-data-export-readme.md?raw';
import towerFluxReadme from '../../../docs/tower-flux-export-readme.md?raw';
import exportMapReadme from '../../../docs/export-map-readme.md?raw';

type Tab = 'map' | 'chamber' | 'manual' | 'sensor' | 'tower';

const TABS: { key: Tab; label: string }[] = [
	{ key: 'map',     label: 'Using the map' },
	{ key: 'chamber', label: 'Chamber Flux' },
	{ key: 'manual',  label: 'Manual Obs' },
	{ key: 'sensor',  label: 'Sensor Data' },
	{ key: 'tower',   label: 'Tower Flux' },
];

const README: Record<Exclude<Tab, 'manual'>, string> = {
	map:     exportMapReadme,
	chamber: chamberFluxReadme,
	sensor:  sensorDataReadme,
	tower:   towerFluxReadme,
};

type Props = { open: boolean; onClose: () => void };

export function ChamberFluxReadmeModal({ open, onClose }: Props) {
	const [activeTab, setActiveTab] = useState<Tab>('map');

	const { data: manualReadme, isLoading: manualReadmeLoading } = useQuery({
		queryKey: ['manual-export-readme'],
		queryFn: () => dataExportGetManualExportReadme(),
		staleTime: Infinity,
		enabled: open,
	});

	const content = activeTab === 'manual'
		? (manualReadme ?? (manualReadmeLoading ? 'Loading readme…' : 'The readme could not be loaded.'))
		: README[activeTab];

	return (
		<Modal open={open} onClose={onClose} size="xl" title="About this export">
			<div className="border-b border-gray-200 px-6">
				<nav className="-mb-px flex gap-4">
					{TABS.map(tab => (
						<button
							key={tab.key}
							onClick={() => setActiveTab(tab.key)}
							className={`whitespace-nowrap border-b-2 py-3 text-sm font-medium transition-colors ${
								activeTab === tab.key
									? 'border-smartfield text-smartfield'
									: 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
							}`}
						>
							{tab.label}
						</button>
					))}
				</nav>
			</div>
			<div className="px-6 py-5">
				<div className="prose prose-sm max-w-none">
					<ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
				</div>
			</div>
		</Modal>
	);
}
